import * as React from "react";

import { useTouchRipple } from "@/components/touch-ripple";
import { MuiTypography } from "./mui-typography";
import { cn } from "@/lib/utils";

import { MUI_TOOLTIP_DELAY_MS, MuiTooltip } from "./mui-tooltip";

// MUI's `Switch` at `size='small'` inside a `FormControlLabel`, with a `body2`
// label and an optional plain tooltip over the whole control — the spec lines
// toolbar's "Show changes from Base" and "Show Target Cost"
// (`shared/components/SwitchChanges.tsx:28-35`, `switch-target-cost.tsx:30-39`),
// which the Editor mounts too (#695).
//
// **Not the vendored shadcn `switch.tsx`**, which is a different drawing (a
// 32×18 pill whose thumb sits inside the track). MUI's thumb is larger than
// its track and overhangs it, and every number below is MUI 9.2.0's own
// (`Switch/Switch.js`), save the one the donor theme overrides:
//
//   - the root: 40×24 at `padding: 7` (the small size), clipping, `z-index: 0`;
//   - the switch base: absolute at the root's top left, `padding: 4`, round,
//     `translateX(16px)` when checked, `left`/`transform` over the theme's
//     `shortest` 150ms; its hover disc `action.active` at the hover opacity
//     (primary at the same opacity when checked);
//   - the thumb: 16px, `currentColor` — white unchecked, primary checked,
//     `grey[100]` disabled, `lighten(primary, 0.62)` disabled and checked —
//     under `shadows[1]`, which the donor theme redraws as a 1px `#e0e0e0`
//     ring (`theme.ts:395-399`);
//   - the track: the root's content box, radius 7, black at 0.38 unchecked,
//     primary at 0.5 checked, 0.12 disabled, `z-index: -1`;
//   - the input: invisible over the base, `left: -100%; width: 300%`, so the
//     whole switch is the hit area.
//
// `FormControlLabel`: inline-flex, centred, `margin-left: -11px; margin-right:
// 16px`, pointer cursor (the default arrow when disabled). The label is the
// donor's `<Typography variant='body2'>` — modsy's own wrapper, not MUI's
// `Typography`, so `FormControlLabel` wraps it in its label span
// (`FormControlLabel.js:171-177`) and a disabled switch greys it to
// `text.disabled` (`:65-69`).

interface MuiSwitchProps {
  label: React.ReactNode;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  disabled?: boolean;
  /** The plain MUI `Tooltip` around the whole control — no arrow, bottom. */
  tooltip?: string;
  className?: string;
  "data-testid"?: string;
}

export function MuiSwitch({
  label,
  checked,
  onCheckedChange,
  disabled = false,
  tooltip,
  className,
  "data-testid": testId,
}: MuiSwitchProps) {
  const { rippleTargetProps, rippleLayer } = useTouchRipple({ center: true });

  const control = (
    <label
      data-slot="mui-switch-label"
      data-testid={testId}
      className={cn(
        "mr-4 -ml-[11px] inline-flex items-center align-middle",
        disabled ? "cursor-default" : "cursor-pointer",
        className,
      )}
    >
      <span
        data-slot="mui-switch"
        className="relative z-0 box-border inline-flex h-6 w-10 shrink-0 overflow-hidden p-[7px] align-middle"
      >
        <span
          data-slot="mui-switch-base"
          data-state={checked ? "checked" : "unchecked"}
          {...(disabled ? {} : rippleTargetProps)}
          className={cn(
            "absolute top-0 left-0 z-1 flex overflow-hidden rounded-full p-1",
            "transition-[left,transform] duration-150 ease-in-out",
            checked && "translate-x-4",
            disabled
              ? checked
                ? "text-mui-switch-primary-disabled"
                : "text-mui-grey-100"
              : checked
                ? "text-lennar-blue-600 hover:bg-lennar-primary-hover"
                : "hover:bg-lennar-action-hover text-white",
          )}
        >
          <input
            type="checkbox"
            role="switch"
            checked={checked}
            disabled={disabled}
            onChange={(event) => onCheckedChange(event.target.checked)}
            className="absolute top-0 -left-full z-1 m-0 h-full w-[300%] cursor-[inherit] p-0 opacity-0"
          />
          <span
            data-slot="mui-switch-thumb"
            className="shadow-modsy-elevation-1 size-4 rounded-full bg-current"
          />
          {rippleLayer}
        </span>
        <span
          data-slot="mui-switch-track"
          aria-hidden="true"
          className={cn(
            "-z-1 size-full rounded-[7px] transition-[opacity,background-color] duration-150 ease-in-out",
            checked ? "bg-lennar-blue-600" : "bg-black",
            disabled ? "opacity-12" : checked ? "opacity-50" : "opacity-38",
          )}
        />
      </span>
      <MuiTypography
        variant="body2"
        component="span"
        className={cn(disabled && "text-lennar-ink-disabled")}
      >
        {label}
      </MuiTypography>
    </label>
  );

  if (!tooltip) return control;
  return (
    <MuiTooltip title={tooltip} delayMs={MUI_TOOLTIP_DELAY_MS} asChild>
      {control}
    </MuiTooltip>
  );
}
